import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock,
  Copy,
  Download,
  FileText,
  Languages,
  List,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Search,
  SkipBack,
  SkipForward,
  Sparkles,
  Star,
  Users,
  Volume1,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, ConfirmDialog, Modal } from '../components/UIComponents';
import EditableFilename from '../components/EditableFilename';
import EditableSegment from '../components/EditableSegment';
import TranslationSettings, { type TranslationDisplayMode } from '../components/TranslationSettings';
import { MindMapViewer } from '../components/MindMapViewer';
import ExportTranscript, { type ExportOptions } from '../components/ExportTranscript';
import {
  buildExportFilename,
  downloadBlob,
  generateJSON,
  generateMarkdown,
  generateSRT,
  generateTXT,
  generateVTT,
} from '../utils/exportUtils';
import { stripKnownTranscriptionTitleExtension } from '../utils/transcriptionTitle';
import { applyTranslationUpdates } from '../utils/translationUpdates';
import {
  getAnonymousQuota,
  getAnonymousDownloadURL,
  getAnonymousSummary,
  getAnonymousKeypoints,
  getAnonymousMindmap,
  getAnonymousTranscription,
  getAnonymousTranscriptionResult,
  getAnonymousTranslation,
  getAnonymousTranslationUpdates,
  getAnonymousTranslationStatus,
  listAnonymousSummaryTemplates,
  listAnonymousTranslations,
  requestAnonymousSummary,
  requestAnonymousKeypoints,
  requestAnonymousMindmap,
  revertAnonymousTranscription,
  translateAnonymousTranscription,
  updateAnonymousTranscriptionContent,
  updateAnonymousTranscriptionDisplayName,
  type AnonymousQuotaStatus,
  type AnonymousTranscription,
  type AnonymousTranscriptionResult,
  getApiErrorCode,
  handleApiError,
} from '../api/anonymous';
import { TrialLimitBanner } from '../components/RegistrationPrompt';
import { TranscriptionSkeletonBody, TranscriptionSkeletonHeader } from '../components/TranscriptionSkeleton';
import ProcessingTimer from '../components/ProcessingTimer';
import { SummaryMarkdown } from '../components/SummaryMarkdown';
import { CopyIconButton } from '../components/CopyIconButton';
import { TemplateSelect } from '../components/TemplateSelect';
import { useTransition } from '../hooks/useTransition';
import { useScrollBoundary, getScrollBehavior } from '../hooks/useScrollBoundary';
import { TranscriptScrollFab } from '../components/TranscriptScrollFab';
import type {
  MindmapResponse,
  KeypointsItem,

  KeypointsResponse,
  KeypointsStatus,
  RevertResponse,
  SummaryResponse,
  SummaryStatus,
  SummaryTemplate,
  TranscriptionSegment,
  TranslationItem,
} from '../api/transcriptions';

const pollIntervalMs = 2000;

// ============================================================================
// Sub-Components (copied from TranscriptionDetail for consistent UI)
// ============================================================================

interface TranscriptHeaderProps {
  fileName: React.ReactNode;
  processingTime?: number;
  isEdited?: boolean;
  isTranslationStale?: boolean;
  onBack: () => void;
  onExport: () => void;
  status?: string;
  startedAt?: string;
  createdAt?: string;
  progress?: number;
}

const TranscriptHeader = ({
  fileName,
  processingTime,
  isEdited,
  isTranslationStale,
  onBack,
  onExport,
  status,
  startedAt,
  createdAt,
  progress,
}: TranscriptHeaderProps) => {
  const { t } = useTranslation();

  const formatProcessingTime = (seconds: number) => {
    if (seconds < 60) return t('transcription.processingTime.seconds', { count: Math.round(seconds) });
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (secs > 0) {
      return t('transcription.processingTime.minutesAndSeconds', { minutes: mins, seconds: secs });
    }
    return t('transcription.processingTime.minutes', { count: mins });
  };

  const isProcessing = ['pending', 'queued', 'processing'].includes(status || '');

  return (
    <div className="flex items-center justify-between py-1 px-1 mb-1">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900">{fileName}</h1>
            {isEdited && <Badge variant="info">{t('transcription.badges.edited')}</Badge>}
            {isTranslationStale && (
              <Badge variant="warning">
                <span className="flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                   {t('transcription.badges.translationOutdated')}
                </span>
              </Badge>
            )}
            {status === 'queued' && <Badge variant="warning">{t('transcription.status.queued')}</Badge>}
            {status === 'pending' && <Badge variant="neutral">{t('transcription.badges.pending')}</Badge>}
            {status === 'processing' && (
              <Badge variant="primary">
                {progress
                  ? t('transcription.badges.processingWithProgress', { percent: Math.round(progress) })
                  : t('transcription.status.processing')}
              </Badge>
            )}
          </div>
          {isProcessing && createdAt ? (
            <div className="mt-1">
              <ProcessingTimer startedAt={startedAt} createdAt={createdAt} />
            </div>
          ) : processingTime ? (
            <div className="flex items-center gap-1 text-sm text-slate-500">
              <Clock className="h-3.5 w-3.5" />
              <span>{t('transcription.processingTime.label', { time: formatProcessingTime(processingTime) })}</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={onExport} disabled={isProcessing}>
          <Download className="h-4 w-4 me-2" />
          {t('transcription.toolbar.exportTranscript')}
        </Button>
      </div>
    </div>
  );
};

interface TranscriptToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCopyAll: () => void;
  showSpeakerButton: boolean;
  onSpeakerClick: () => void;
  isEdited: boolean;
  onRevert: () => void;
  isReverting?: boolean;
  isTranslationOpen: boolean;
  onToggleTranslationOpen: () => void;
  translationSettings: {
    targetLanguage: string;
    onTargetLanguageChange: (value: string) => void;
    displayMode: TranslationDisplayMode;
    onDisplayModeChange: (mode: TranslationDisplayMode) => void;
    onTranslate: () => void;
    isTranslating?: boolean;
    translationProgress?: number;
    hasTranslation?: boolean;
    isTranslationStale?: boolean;
    errorMessage?: string | null;
    languageOptions: Array<{ value: string; label: string }>;
  };
}

const TranscriptToolbar = ({
  searchValue,
  onSearchChange,
  onCopyAll,
  showSpeakerButton,
  onSpeakerClick,
  isEdited,
  onRevert,
  isReverting = false,
  isTranslationOpen,
  onToggleTranslationOpen,
  translationSettings,
}: TranscriptToolbarProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const translationRef = useRef<HTMLDivElement>(null);
  const { shouldMount, isVisible } = useTransition(isTranslationOpen, 200);

  useEffect(() => {
    if (!isTranslationOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!translationRef.current) return;
      if (!translationRef.current.contains(event.target as Node)) {
        onToggleTranslationOpen();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isTranslationOpen, onToggleTranslationOpen]);

  const handleCopy = () => {
    onCopyAll();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-200">
      <h2 className="text-base font-semibold text-slate-900">{t('transcription.toolbar.transcriptTitle')}</h2>
      <div className="flex items-center gap-1">
        <div className="relative">
          <Search className="absolute start-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('transcription.toolbar.searchPlaceholder')}
            className="ps-8 pe-3 py-1.5 text-sm w-32 border border-transparent rounded-md focus:outline-none focus:border-slate-300 focus:w-48 transition-all bg-transparent hover:bg-slate-50 focus:bg-white"
          />
        </div>
        {showSpeakerButton && (
          <button
            type="button"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title={t('transcription.toolbar.speakerIdentificationTitle')}
            onClick={onSpeakerClick}
          >
            <Users className="h-4 w-4 text-slate-500" />
          </button>
        )}
        <button
          className={`p-2 hover:bg-slate-100 rounded-lg transition-colors ${copied ? 'text-green-600' : ''}`}
          title={copied ? t('common.copied') : t('transcription.toolbar.copyAll')}
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4 text-slate-500" />
          )}
        </button>
        <div className="relative" ref={translationRef}>
          <button
            className={`p-2 hover:bg-slate-100 rounded-lg transition-colors ${
              isTranslationOpen ? 'bg-slate-100' : ''
            }`}
            title={t('transcription.tabs.translation')}
            onClick={(event) => {
              event.stopPropagation();
              onToggleTranslationOpen();
            }}
          >
          <Languages className="h-4 w-4 text-slate-500" />
        </button>
        {shouldMount && (
          <div className={`absolute right-0 top-full mt-2 z-20 ${isVisible ? 'dropdown-in' : 'dropdown-out'}`}>
            <TranslationSettings {...translationSettings} />
          </div>
        )}
      </div>

        {isEdited && (
          <button
            className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-60"
             title={t('transcription.toolbar.revertAllEditsTitle')}
            onClick={onRevert}
            disabled={isReverting}
          >
            <RotateCcw className={`h-3.5 w-3.5 ${isReverting ? 'animate-spin' : ''}`} />
            {t('transcription.toolbar.revertConfirm')}
          </button>
        )}
      </div>
    </div>
  );
};

interface QualityRatingProps {
  onDismiss: () => void;
}

const QualityRating = ({ onDismiss }: QualityRatingProps) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-600">{t('transcription.toolbar.ratingTitle')}</span>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              onClick={() => setRating(star)}
              className="p-0.5 transition-colors"
              type="button"
            >
              <Star
                className={`h-5 w-5 ${
                  star <= (hoveredRating || rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
      <button onClick={onDismiss} className="p-1 hover:bg-slate-200 rounded transition-colors" type="button">
        <X className="h-4 w-4 text-slate-400" />
      </button>
    </div>
  );
};

interface TrialAccountBannerProps {
  onDismiss?: () => void;
  onRegister: () => void;
  onLogin: () => void;
}

const TrialAccountBanner = ({ onDismiss, onRegister, onLogin }: TrialAccountBannerProps) => {
  const { t } = useTranslation();
  return (
    <div className="relative flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-600 text-white rounded-lg">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <span className="text-sm font-medium">{t('app.trial.banner.title')}</span>
          <p className="text-xs text-white/70">{t('app.trial.banner.subtitle')}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="bg-white text-indigo-600 border-white hover:bg-indigo-50"
          onClick={onRegister}
        >
          {t('app.trial.banner.createAccount')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10"
          onClick={onLogin}
        >
          {t('app.trial.banner.signIn')}
        </Button>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label={t('app.trial.banner.dismiss')}
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

interface AudioPlayerBarProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackSpeed: number;
  volume: number;
  onPlayPause: () => void;
  onSkipBack: () => void;
  onSkipForward: () => void;
  onSeek: (time: number) => void;
  onSpeedChange: (speed: number) => void;
  onVolumeChange: (volume: number) => void;
}

const AudioPlayerBar = ({
  isPlaying,
  currentTime,
  duration,
  playbackSpeed,
  volume,
  onPlayPause,
  onSkipBack,
  onSkipForward,
  onSeek,
  onSpeedChange,
  onVolumeChange,
}: AudioPlayerBarProps) => {
  const { t } = useTranslation();
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  const VolumeIcon = volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-4 px-4 py-3 bg-white border-t border-slate-200">
      <div className="flex items-center gap-2">
        <button
          onClick={onSkipBack}
          className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          title={t('transcription.toolbar.skipBack', { seconds: 10 })}
          type="button"
        >
          <SkipBack className="h-4 w-4 text-slate-600" />
        </button>
        <button
          onClick={onPlayPause}
          className="p-3 bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors"
          type="button"
        >
          {isPlaying ? (
            <Pause className="h-5 w-5 text-white" fill="white" />
          ) : (
            <Play className="h-5 w-5 text-white ml-0.5" fill="white" />
          )}
        </button>
        <button
          onClick={onSkipForward}
          className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          title={t('transcription.toolbar.skipForward', { seconds: 10 })}
          type="button"
        >
          <SkipForward className="h-4 w-4 text-slate-600" />
        </button>
      </div>

      <div className="flex-1 flex items-center gap-2">
        <span className="text-xs text-slate-500 w-10">{formatTime(currentTime)}</span>
        <div className="flex-1 relative h-1 bg-slate-200 rounded-full cursor-pointer">
          <div className="absolute h-full bg-indigo-600 rounded-full" style={{ width: `${progress}%` }} />
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={(e) => onSeek(parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <span className="text-xs text-slate-500 w-10">{formatTime(duration)}</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative">
          <button
            onClick={() => setShowVolumeSlider((prev) => !prev)}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            title={t('transcription.toolbar.volume')}
            type="button"
          >
            <VolumeIcon className="h-4 w-4 text-slate-600" />
          </button>
          {showVolumeSlider && (
            <div className="absolute bottom-full right-0 mb-2 bg-white p-3 rounded-lg shadow-lg border border-slate-200">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                className="w-20"
              />
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setShowSpeedMenu((prev) => !prev)}
            className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            title={t('transcription.toolbar.playbackSpeed')}
            type="button"
          >
            {playbackSpeed}x
          </button>
          {showSpeedMenu && (
            <div className="absolute bottom-full right-0 mb-2 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
              {speedOptions.map((speed) => (
                <button
                  key={speed}
                  onClick={() => {
                    onSpeedChange(speed);
                    setShowSpeedMenu(false);
                  }}
                  className={`w-full px-4 py-2 text-xs text-left hover:bg-slate-50 transition-colors ${
                    speed === playbackSpeed ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-slate-600'
                  }`}
                  type="button"
                >
                  {speed}x
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type MindmapUIStatus = 'idle' | 'pending' | 'processing' | 'completed' | 'failed';
type SummaryUIStatus = 'idle' | SummaryStatus;
type KeypointsUIStatus = 'idle' | KeypointsStatus;

interface AIInsightsPanelProps {
  activeTab: 'overview' | 'keypoints' | 'mindmap';
  onTabChange: (tab: 'overview' | 'keypoints' | 'mindmap') => void;
  summaryTemplates: SummaryTemplate[];
  selectedSummaryTemplateId: string;
  onSelectSummaryTemplateId: (templateId: string) => void;
  summaryStatus: SummaryUIStatus;
  summaryMarkdown: string | null;
  summaryError: string | null;
  onGenerateSummary: () => void;
  keypointsStatus: KeypointsUIStatus;
  keypointsItems: KeypointsItem[];
  keypointsError: string | null;
  mindmapStatus: MindmapUIStatus;
  mindmapMarkdown: string | null;
  mindmapError: string | null;
  onGenerateMindmap: () => void;
  onGenerateKeyPoints: () => void;
}

const AIInsightsPanel = ({
  activeTab,
  onTabChange,
  summaryTemplates,
  selectedSummaryTemplateId,
  onSelectSummaryTemplateId,
  summaryStatus,
  summaryMarkdown,
  summaryError,
  onGenerateSummary,
  keypointsStatus,
  keypointsItems,
  keypointsError,
  mindmapStatus,
  mindmapMarkdown,
  mindmapError,
  onGenerateMindmap,
  onGenerateKeyPoints,
}: AIInsightsPanelProps) => {
  const { t } = useTranslation();
  const selectedTemplate = summaryTemplates.find(t => t.id === selectedSummaryTemplateId) || null;
  const keypointsCopyText = keypointsItems
    .map((it, idx) => `Q${idx + 1}: ${it.question}\nA${idx + 1}: ${it.answer}`)
    .join('\n\n');

  return (
    <div className="h-full flex flex-col bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
        <button
          onClick={() => onTabChange('overview')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'overview'
              ? 'bg-indigo-100 text-indigo-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          type="button"
        >
          {t('transcription.aiInsights.tabs.overview')}
        </button>
        <button
          onClick={() => onTabChange('keypoints')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'keypoints'
              ? 'bg-indigo-100 text-indigo-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          type="button"
        >
          {t('transcription.aiInsights.tabs.keypoints')}
        </button>
        <button
          onClick={() => onTabChange('mindmap')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'mindmap'
              ? 'bg-indigo-100 text-indigo-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          type="button"
        >
          {t('transcription.aiInsights.tabs.mindmap')}
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="flex-1 overflow-hidden p-4 tab-fade-slide flex flex-col min-h-0">
          <div className="flex flex-col min-h-0 flex-1">
            <div className="flex flex-col min-h-0 flex-1">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-sm font-semibold text-slate-900">{t('transcription.tabs.summary')}</h3>
                <div className="flex items-center gap-2">
                  {summaryTemplates.length > 0 && (
                    <TemplateSelect
                      options={summaryTemplates}
                      value={selectedSummaryTemplateId}
                      onChange={onSelectSummaryTemplateId}
                    />
                  )}
                  {summaryStatus === 'completed' && summaryMarkdown && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onGenerateSummary}
                        title={t('transcription.toolbar.regenerate')}
                        type="button"
                      >
                        <RefreshCw className="h-5 w-5" />
                      </Button>
                      <CopyIconButton text={summaryMarkdown} title={t('transcription.toolbar.copySummary')} />
                    </>
                  )}
                </div>
              </div>

              {summaryStatus === 'idle' && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500 max-w-[260px] mb-4">
                    {selectedTemplate
                      ? t('transcription.toolbar.summaryIdleWithTemplate')
                      : t('transcription.toolbar.summaryIdleNoTemplate')}
                  </p>
                  <Button variant="primary" size="sm" onClick={onGenerateSummary} type="button">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate
                  </Button>
                </div>
              )}

              {(summaryStatus === 'pending' || summaryStatus === 'processing') && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="relative mb-4">
                    <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                      <RefreshCw className="h-8 w-8 text-indigo-600 animate-spin" />
                    </div>
                    <div className="absolute inset-0 rounded-2xl bg-indigo-100/50 animate-pulse" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">{t('transcription.aiInsights.summary.processing.title')}</p>
                  <p className="mt-1 text-xs text-slate-500">{t('transcription.aiInsights.summary.processing.description')}</p>
                </div>
              )}

              {summaryStatus === 'failed' && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                    <AlertTriangle className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 mb-1">{t('transcription.aiInsights.summary.failed.title')}</p>
                  <p className="text-xs text-slate-500 max-w-[280px] mb-4">{summaryError || t('transcription.aiInsights.summary.failed.fallbackError')}</p>
                  <Button variant="primary" size="sm" onClick={onGenerateSummary} type="button">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('transcription.aiInsights.summary.failed.retry')}
                  </Button>
                </div>
              )}

              {summaryStatus === 'completed' && summaryMarkdown && (
                <div className="flex-1 min-h-0">
                  <SummaryMarkdown markdown={summaryMarkdown} />
                </div>
              )}

              {summaryStatus === 'completed' && !summaryMarkdown && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                    <AlertTriangle className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 mb-1">{t('transcription.aiInsights.summary.empty.title')}</p>
                  <p className="text-xs text-slate-500 max-w-[280px] mb-4">{t('transcription.aiInsights.summary.empty.description')}</p>
                  <Button variant="primary" size="sm" onClick={onGenerateSummary} type="button">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('transcription.aiInsights.summary.empty.generate')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'keypoints' && (
        <div className="flex-1 overflow-y-auto p-4 tab-fade-slide">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">{t('transcription.aiInsights.keypoints.title')}</h3>
              <div className="flex items-center gap-2">
                {keypointsStatus === 'completed' && keypointsItems.length > 0 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onGenerateKeyPoints}
                      title={t('transcription.toolbar.regenerate')}
                      type="button"
                    >
                      <RefreshCw className="h-5 w-5" />
                    </Button>
                    <CopyIconButton text={keypointsCopyText} title={t('transcription.toolbar.copyKeyPoints')} />
                  </>
                )}
                {(keypointsStatus === 'idle' || keypointsStatus === 'failed') && (
                  <Button variant="primary" size="sm" onClick={onGenerateKeyPoints} type="button">
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t('transcription.aiInsights.keypoints.generate')}
                  </Button>
                )}
              </div>
            </div>

            {keypointsStatus === 'idle' && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                  <List className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-sm text-slate-500 max-w-[320px]">
                  {t('transcription.aiInsights.keypoints.idleDescription')}
                </p>
              </div>
            )}

            {(keypointsStatus === 'pending' || keypointsStatus === 'processing') && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="relative mb-4">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                    <RefreshCw className="h-8 w-8 text-indigo-600 animate-spin" />
                  </div>
                  <div className="absolute inset-0 rounded-2xl bg-indigo-100/50 animate-pulse" />
                </div>
                <p className="text-sm font-medium text-slate-900">{t('transcription.aiInsights.keypoints.processing.title')}</p>
                <p className="mt-1 text-xs text-slate-500">{t('transcription.aiInsights.keypoints.processing.description')}</p>
              </div>
            )}

            {keypointsStatus === 'failed' && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-1">{t('transcription.aiInsights.keypoints.failed.title')}</p>
                <p className="text-xs text-slate-500 max-w-[320px] mb-4">{keypointsError || t('transcription.aiInsights.keypoints.failed.fallbackError')}</p>
                <Button variant="primary" size="sm" onClick={onGenerateKeyPoints} type="button">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            )}

            {keypointsStatus === 'completed' && keypointsItems.length > 0 && (
              <div className="space-y-3">
                {keypointsItems.map((it, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-sm font-semibold text-slate-900 mb-2">{it.question}</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{it.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {keypointsStatus === 'completed' && keypointsItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-1">Key points are empty</p>
                <p className="text-xs text-slate-500 max-w-[320px] mb-4">Please try regenerating.</p>
                <Button variant="primary" size="sm" onClick={onGenerateKeyPoints} type="button">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'mindmap' && (
        <div className="relative flex-1 overflow-hidden tab-fade-slide">
          {(mindmapStatus === 'pending' || mindmapStatus === 'processing') && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="relative mb-4">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <RefreshCw className="h-8 w-8 text-indigo-600 animate-spin" />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-indigo-100/50 animate-pulse" />
              </div>
              <p className="text-sm font-medium text-slate-900">{t('transcription.aiInsights.mindmap.processing.title')}</p>
              <p className="mt-1 text-xs text-slate-500">{t('transcription.aiInsights.mindmap.processing.description')}</p>
              <div className="mt-4 flex items-center gap-1">
                <span
                  className="h-2 w-2 rounded-full bg-indigo-300 animate-bounce"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-indigo-300 animate-bounce"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-indigo-300 animate-bounce"
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          )}

          {mindmapStatus === 'idle' && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <svg
                  className="h-8 w-8 text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="3" />
                  <circle cx="5" cy="6" r="2" />
                  <circle cx="19" cy="6" r="2" />
                  <circle cx="5" cy="18" r="2" />
                  <circle cx="19" cy="18" r="2" />
                  <line x1="9.5" y1="10" x2="6.5" y2="7.5" />
                  <line x1="14.5" y1="10" x2="17.5" y2="7.5" />
                  <line x1="9.5" y1="14" x2="6.5" y2="16.5" />
                  <line x1="14.5" y1="14" x2="17.5" y2="16.5" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 max-w-[260px] mb-4">
                {t('transcription.aiInsights.mindmap.idleDescription')}
              </p>
              <Button variant="primary" size="sm" onClick={onGenerateMindmap}>
                <Sparkles className="h-4 w-4 mr-2" />
                {t('transcription.aiInsights.mindmap.idle.generate')}
              </Button>
            </div>
          )}

          {mindmapStatus === 'failed' && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">{t('transcription.aiInsights.mindmap.failed.title')}</p>
              <p className="text-xs text-slate-500 max-w-[280px] mb-4">{mindmapError || t('transcription.aiInsights.mindmap.failed.fallbackError')}</p>
              <Button variant="primary" size="sm" onClick={onGenerateMindmap}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('transcription.aiInsights.mindmap.failed.retry')}
              </Button>
            </div>
          )}

          {mindmapStatus === 'completed' && mindmapMarkdown && (
            <>
              <div className="absolute top-3 right-3 z-10">
                <Button variant="outline" size="sm" onClick={onGenerateMindmap}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('transcription.aiInsights.mindmap.toolbar.regenerate')}
                </Button>
              </div>
              <MindMapViewer markdown={mindmapMarkdown} />
            </>
          )}

          {mindmapStatus === 'completed' && !mindmapMarkdown && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">{t('transcription.aiInsights.mindmap.empty.title')}</p>
              <p className="text-xs text-slate-500 max-w-[280px] mb-4">{t('transcription.aiInsights.mindmap.empty.description')}</p>
              <Button variant="primary" size="sm" onClick={onGenerateMindmap}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('transcription.aiInsights.mindmap.empty.generate')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface PanelResizerProps {
  onResize: (delta: number) => void;
}

const PanelResizer = ({ onResize }: PanelResizerProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startXRef.current;
      startXRef.current = moveEvent.clientX;
      onResize(delta);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      className={`hidden lg:flex w-2 flex-shrink-0 items-center justify-center cursor-col-resize group ${
        isDragging ? 'bg-indigo-100' : 'hover:bg-slate-100'
      } transition-colors rounded`}
      onMouseDown={handleMouseDown}
    >
      <div
        className={`w-1 h-12 rounded-full transition-colors ${
          isDragging ? 'bg-indigo-400' : 'bg-slate-300 group-hover:bg-slate-400'
        }`}
      />
    </div>
  );
};

interface ToastProps {
  message: string;
  isVisible: boolean;
}

const Toast = ({ message, isVisible: propIsVisible }: ToastProps) => {
  const { shouldMount, isVisible } = useTransition(propIsVisible, 300);

  if (!shouldMount) return null;

  return (
    <div className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-50 ${isVisible ? 'toast-in' : 'toast-out'}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg shadow-lg">
        <Check className="h-4 w-4 text-green-400" />
        <span className="text-sm font-medium">{message}</span>
      </div>
    </div>
  );
};

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
  { value: 'th', label: 'Thai' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ms', label: 'Malay' },
  { value: 'tr', label: 'Turkish' },
  { value: 'pl', label: 'Polish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'sv', label: 'Swedish' },
  { value: 'da', label: 'Danish' },
  { value: 'fi', label: 'Finnish' },
  { value: 'no', label: 'Norwegian' },
  { value: 'cs', label: 'Czech' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'el', label: 'Greek' },
  { value: 'he', label: 'Hebrew' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ro', label: 'Romanian' },
  { value: 'bg', label: 'Bulgarian' },
];

function mapAnonymousSegments(
  result: AnonymousTranscriptionResult | null,
  fallbackDurationSeconds = 0
): TranscriptionSegment[] {
  if (result?.segments?.length) {
    return result.segments.map((seg) => ({
      id: seg.id,
      start: seg.start,
      end: seg.end,
      text: seg.text,
      is_modified: seg.is_modified,
      speaker: seg.speaker,
      confidence: seg.confidence,
    }));
  }

  const content = result?.content?.trim();
  if (!content) return [];

  const durationSeconds = result?.metadata?.duration ?? fallbackDurationSeconds;
  return [
    {
      id: 0,
      start: 0,
      end: durationSeconds,
      text: content,
    },
  ];
}

function calculateProcessingTimeSeconds(t: AnonymousTranscription | null): number | undefined {
  if (!t?.started_at || !t?.completed_at) return undefined;
  const started = Date.parse(t.started_at);
  const completed = Date.parse(t.completed_at);
  if (!Number.isFinite(started) || !Number.isFinite(completed)) return undefined;
  const diffSeconds = (completed - started) / 1000;
  if (!Number.isFinite(diffSeconds) || diffSeconds <= 0) return undefined;
  return diffSeconds;
}

export default function TrialTranscriptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<AnonymousQuotaStatus | null>(null);
  const [transcription, setTranscription] = useState<AnonymousTranscription | null>(null);
  const [segments, setSegments] = useState<TranscriptionSegment[]>([]);

  const hasSpeakerInformation = useMemo(() => {
    for (const seg of segments) {
      if ((seg.speaker ?? '').trim() !== '') return true;
    }
    return false;
  }, [segments]);

  const [displayMode, setDisplayMode] = useState<TranslationDisplayMode>('bilingual');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [isTranslationOpen, setIsTranslationOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [availableTranslations, setAvailableTranslations] = useState<TranslationItem[]>([]);
  const [isTranslationStale, setIsTranslationStale] = useState(false);
  const translationPollingRef = useRef<number | null>(null);
  const translationUpdatesCursorRef = useRef(0);
  const userChangedDisplayModeRef = useRef(false);
  const segmentIndexByIdRef = useRef<Map<number, number>>(new Map());

  const [activeSegment, setActiveSegment] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [leftPanelWidth, setLeftPanelWidth] = useState(60);

  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  const [showRating, setShowRating] = useState(true);
  const [showAccountBanner, setShowAccountBanner] = useState(true);

  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSpeakerRenamePromptOpen, setIsSpeakerRenamePromptOpen] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const segmentsListRef = useRef<HTMLDivElement>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [editingSegmentId, setEditingSegmentId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [isRenamingDisplayName, setIsRenamingDisplayName] = useState(false);

  const [aiTab, setAiTab] = useState<'overview' | 'keypoints' | 'mindmap'>('overview');
  const [mindmapStatus, setMindmapStatus] = useState<MindmapUIStatus>('idle');
  const [mindmapMarkdown, setMindmapMarkdown] = useState<string | null>(null);
  const [mindmapError, setMindmapError] = useState<string | null>(null);
  const mindmapPollingRef = useRef<number | null>(null);

  const [summaryTemplates, setSummaryTemplates] = useState<SummaryTemplate[]>([]);
  const [selectedSummaryTemplateId, setSelectedSummaryTemplateId] = useState('');
  const [summaryStatus, setSummaryStatus] = useState<SummaryUIStatus>('idle');
  const [summaryMarkdown, setSummaryMarkdown] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const summaryPollingRef = useRef<number | null>(null);

  const [keypointsStatus, setKeypointsStatus] = useState<KeypointsUIStatus>('idle');
  const [keypointsItems, setKeypointsItems] = useState<KeypointsItem[]>([]);
  const [keypointsError, setKeypointsError] = useState<string | null>(null);
  const keypointsPollingRef = useRef<number | null>(null);

  const pollingRef = useRef<number | null>(null);
  const resultLoadedRef = useRef(false);
  const audioUrlLoadedRef = useRef(false);
  const translationsLoadedRef = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const processingTimeSeconds = useMemo(() => calculateProcessingTimeSeconds(transcription), [transcription]);
  const outputLanguage = useMemo(() => (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en'), []);
  const showToastNotification = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    window.setTimeout(() => setShowToast(false), 2000);
  }, []);

  const handleCopyAll = useCallback(() => {
    const allText = segments.map((s) => s.text).join(' ');
    navigator.clipboard.writeText(allText + ' \n\nTranscribed by OmniScribe.');
    showToastNotification('Transcript copied to clipboard');
  }, [segments, showToastNotification]);

  const { isScrollable, isAtBottom, update: updateScrollBoundary } = useScrollBoundary(segmentsListRef);

  const handleScrollToTop = useCallback(() => {
    segmentsListRef.current?.scrollTo({ top: 0, behavior: getScrollBehavior() });
  }, []);

  const handleScrollToBottom = useCallback(() => {
    const el = segmentsListRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: getScrollBehavior() });
  }, []);

  useEffect(() => {
    updateScrollBoundary();
  }, [segments.length, updateScrollBoundary]);

  const handlePanelResize = useCallback(
    (delta: number) => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const deltaPercent = (delta / containerWidth) * 100;
      setLeftPanelWidth((prev) => Math.min(80, Math.max(30, prev + deltaPercent)));
    },
    []
  );

  const stopTranslationPolling = useCallback(() => {
    if (translationPollingRef.current) {
      window.clearInterval(translationPollingRef.current);
      translationPollingRef.current = null;
    }
  }, []);

  const fetchAvailableTranslations = useCallback(async (transcriptionId: string) => {
    try {
      const response = await listAnonymousTranslations(transcriptionId);
      setAvailableTranslations(response.translations || []);
      return response.translations || [];
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch available translations:', err);
      }
      return [];
    }
  }, []);

  const fetchTranslationContent = useCallback(async (transcriptionId: string, lang: string) => {
    try {
      const translationFile = await getAnonymousTranslation(transcriptionId, lang);
      setIsTranslationStale(translationFile.is_stale);

      if (translationFile.segments && translationFile.segments.length > 0) {
        setSegments((prevSegments) => {
          const translationMap = new Map(translationFile.segments.map((ts) => [ts.id, ts.translation]));
          return prevSegments.map((segment) => ({
            ...segment,
            translation: translationMap.get(segment.id) || segment.translation,
          }));
        });

        if (!userChangedDisplayModeRef.current) {
          setDisplayMode((prev) => (prev === 'original' ? 'bilingual' : prev));
        }
      }

      return translationFile;
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch translation content:', err);
      }
      return null;
    }
  }, []);

  const startTranslationPolling = useCallback(
    (transcriptionId: string, lang: string) => {
      stopTranslationPolling();
      translationUpdatesCursorRef.current = 0;

      const pollUpdates = async () => {
        try {
          const updates = await getAnonymousTranslationUpdates(
            transcriptionId,
            lang,
            translationUpdatesCursorRef.current,
            200
          );

          if (updates.progress !== undefined) {
            setTranslationProgress(updates.progress);
          }

          if (updates.translations && updates.translations.length > 0) {
            setSegments((prevSegments) =>
              applyTranslationUpdates(prevSegments, updates.translations, segmentIndexByIdRef.current)
            );

            if (!userChangedDisplayModeRef.current) {
              setDisplayMode((prev) => (prev === 'original' ? 'bilingual' : prev));
            }
          }

          translationUpdatesCursorRef.current = updates.next_cursor ?? translationUpdatesCursorRef.current;

          if (updates.status === 'completed') {
            stopTranslationPolling();
            await fetchTranslationContent(transcriptionId, lang);
            await fetchAvailableTranslations(transcriptionId);
            setIsTranslating(false);
            setTranslationProgress(100);
            showToastNotification('Translation completed');
          } else if (updates.status === 'failed') {
            stopTranslationPolling();
            setTranslationError('Translation failed. Please try again.');
            setIsTranslating(false);
            setTranslationProgress(0);
            showToastNotification('Translation failed');
          }
        } catch (err) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('Failed to poll translation updates:', err);
          }
        }
      };

      translationPollingRef.current = window.setInterval(pollUpdates, 2000);
      void pollUpdates();
    },
    [fetchAvailableTranslations, fetchTranslationContent, showToastNotification, stopTranslationPolling]
  );

  useEffect(() => {
    return () => stopTranslationPolling();
  }, [stopTranslationPolling]);

  const stopSummaryPolling = useCallback(() => {
    if (summaryPollingRef.current) {
      window.clearInterval(summaryPollingRef.current);
      summaryPollingRef.current = null;
    }
  }, []);

  const applySummaryResponse = useCallback((resp: SummaryResponse) => {
    setSummaryStatus(resp.status);
    setSummaryError(resp.error || null);

    if (resp.status === 'completed') {
      setSummaryMarkdown(resp.summary_markdown || '');
      return;
    }

    setSummaryMarkdown(null);
  }, []);

  const loadSummaryTemplates = useCallback(async () => {
    const resp = await listAnonymousSummaryTemplates();
    const templates = resp.templates || [];
    setSummaryTemplates(templates);

    if (!selectedSummaryTemplateId && templates.length > 0) {
      setSelectedSummaryTemplateId(templates[0].id);
    }

    if (selectedSummaryTemplateId && !templates.some(t => t.id === selectedSummaryTemplateId) && templates.length > 0) {
      setSelectedSummaryTemplateId(templates[0].id);
    }
  }, [selectedSummaryTemplateId]);

  const fetchSummary = useCallback(async (transcriptionId: string, templateId: string) => {
    try {
      const resp = await getAnonymousSummary(transcriptionId, templateId);
      applySummaryResponse(resp);
      return resp;
    } catch {
      // Any error when fetching existing summary means no summary exists yet.
      // Show idle state so user can generate one. Only requestSummary failures
      // should show the failed state.
      setSummaryStatus('idle');
      setSummaryMarkdown(null);
      setSummaryError(null);
      return null;
    }
  }, [applySummaryResponse]);

  const startSummaryPolling = useCallback((transcriptionId: string, templateId: string) => {
    stopSummaryPolling();

    const pollStatus = async () => {
      const resp = await fetchSummary(transcriptionId, templateId);
      if (!resp) {
        stopSummaryPolling();
        return;
      }

      if (resp.status === 'completed' || resp.status === 'failed') {
        stopSummaryPolling();
      }
    };

    summaryPollingRef.current = window.setInterval(pollStatus, pollIntervalMs);
    void pollStatus();
  }, [fetchSummary, stopSummaryPolling]);

  const requestSummary = useCallback(async () => {
    if (!id) return;
    if (!selectedSummaryTemplateId) {
      showToastNotification('Please select a summary template');
      return;
    }

    setSummaryError(null);
    setSummaryStatus('pending');
    setSummaryMarkdown(null);

    try {
      const resp = await requestAnonymousSummary(id, selectedSummaryTemplateId);
      applySummaryResponse(resp);

      if (resp.status === 'pending' || resp.status === 'processing') {
        startSummaryPolling(id, selectedSummaryTemplateId);
      }
    } catch (err) {
      setSummaryStatus('failed');
      setSummaryMarkdown(null);
      setSummaryError(handleApiError(err));
    }
  }, [applySummaryResponse, id, selectedSummaryTemplateId, showToastNotification, startSummaryPolling]);

  const stopKeypointsPolling = useCallback(() => {
    if (keypointsPollingRef.current) {
      window.clearInterval(keypointsPollingRef.current);
      keypointsPollingRef.current = null;
    }
  }, []);

  const applyKeypointsResponse = useCallback((resp: KeypointsResponse) => {
    setKeypointsStatus(resp.status);
    setKeypointsItems(resp.items || []);
    setKeypointsError(resp.error || null);
  }, []);

  const fetchKeypoints = useCallback(async (transcriptionId: string) => {
    try {
      const resp = await getAnonymousKeypoints(transcriptionId, outputLanguage);
      applyKeypointsResponse(resp);
      return resp;
    } catch {
      // Any error when fetching existing keypoints means none exist yet.
      // Show idle state so user can generate them.
      setKeypointsStatus('idle');
      setKeypointsItems([]);
      setKeypointsError(null);
      return null;
    }
  }, [applyKeypointsResponse, outputLanguage]);

  const startKeypointsPolling = useCallback((transcriptionId: string) => {
    stopKeypointsPolling();

    const pollStatus = async () => {
      const resp = await fetchKeypoints(transcriptionId);
      if (!resp) {
        stopKeypointsPolling();
        return;
      }

      if (resp.status === 'completed' || resp.status === 'failed') {
        stopKeypointsPolling();
      }
    };

    keypointsPollingRef.current = window.setInterval(pollStatus, pollIntervalMs);
    void pollStatus();
  }, [fetchKeypoints, stopKeypointsPolling]);

  const requestKeypoints = useCallback(async () => {
    if (!id) return;
    if (transcription?.status !== 'completed') return;

    setKeypointsError(null);
    setKeypointsStatus('pending');
    setKeypointsItems([]);

    try {
      const resp = await requestAnonymousKeypoints(id, outputLanguage);
      applyKeypointsResponse(resp);

      if (resp.status === 'pending' || resp.status === 'processing') {
        startKeypointsPolling(id);
      }
    } catch (err) {
      setKeypointsStatus('failed');
      setKeypointsItems([]);
      setKeypointsError(handleApiError(err));
    }
  }, [applyKeypointsResponse, id, outputLanguage, startKeypointsPolling, transcription?.status]);

  const stopMindmapPolling = useCallback(() => {
    if (mindmapPollingRef.current) {
      window.clearInterval(mindmapPollingRef.current);
      mindmapPollingRef.current = null;
    }
  }, []);

  const applyMindmapResponse = useCallback((resp: MindmapResponse) => {
    setMindmapStatus(resp.status);
    setMindmapError(resp.error || null);

    if (resp.status === 'completed') {
      setMindmapMarkdown(resp.outline_markdown || '');
      return;
    }

    setMindmapMarkdown(null);
  }, []);

  const fetchMindmap = useCallback(
    async (transcriptionId: string) => {
      try {
        const resp = await getAnonymousMindmap(transcriptionId);
        applyMindmapResponse(resp);
        return resp;
      } catch {
        // Any error when fetching existing mindmap means none exists yet.
        // Show idle state so user can generate one.
        setMindmapStatus('idle');
        setMindmapMarkdown(null);
        setMindmapError(null);
        return null;
      }
    },
    [applyMindmapResponse]
  );

  const startMindmapPolling = useCallback(
    (transcriptionId: string) => {
      stopMindmapPolling();

      const pollStatus = async () => {
        const resp = await fetchMindmap(transcriptionId);
        if (!resp) {
          stopMindmapPolling();
          return;
        }

        if (resp.status === 'completed' || resp.status === 'failed') {
          stopMindmapPolling();
        }
      };

      mindmapPollingRef.current = window.setInterval(pollStatus, 2000);
      void pollStatus();
    },
    [fetchMindmap, stopMindmapPolling]
  );

  const requestMindmap = useCallback(async () => {
    if (!id) return;

    setMindmapError(null);
    setMindmapStatus('pending');
    setMindmapMarkdown(null);

    try {
      const resp = await requestAnonymousMindmap(id);
      applyMindmapResponse(resp);

      if (resp.status === 'pending' || resp.status === 'processing') {
        startMindmapPolling(id);
      }
    } catch (err) {
      setMindmapStatus('failed');
      setMindmapMarkdown(null);
      setMindmapError(handleApiError(err));
    }
  }, [applyMindmapResponse, id, startMindmapPolling]);

  useEffect(() => {
    if (aiTab !== 'mindmap') {
      stopMindmapPolling();
    }
  }, [aiTab, stopMindmapPolling]);

  useEffect(() => {
    if (aiTab !== 'overview') {
      stopSummaryPolling();
    }
  }, [aiTab, stopSummaryPolling]);

  useEffect(() => {
    if (aiTab !== 'keypoints') {
      stopKeypointsPolling();
    }
  }, [aiTab, stopKeypointsPolling]);

  useEffect(() => {
    loadSummaryTemplates().catch((err) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load summary templates:', err);
      }
    });
  }, [loadSummaryTemplates]);

  useEffect(() => {
    if (!id || loading || aiTab !== 'overview' || !selectedSummaryTemplateId) return;
    if (transcription?.status !== 'completed') return;

    const load = async () => {
      const resp = await fetchSummary(id, selectedSummaryTemplateId);
      if (resp && (resp.status === 'pending' || resp.status === 'processing')) {
        startSummaryPolling(id, selectedSummaryTemplateId);
      }
    };

    void load();
  }, [aiTab, fetchSummary, id, loading, selectedSummaryTemplateId, startSummaryPolling, transcription?.status]);

  useEffect(() => {
    if (!id || loading || aiTab !== 'keypoints') return;
    if (transcription?.status !== 'completed') return;

    const load = async () => {
      const resp = await fetchKeypoints(id);
      if (resp && (resp.status === 'pending' || resp.status === 'processing')) {
        startKeypointsPolling(id);
      }
    };

    void load();
  }, [aiTab, fetchKeypoints, id, loading, startKeypointsPolling, transcription?.status]);

  useEffect(() => {
    if (!id || loading || aiTab !== 'mindmap') return;

    const load = async () => {
      const resp = await fetchMindmap(id);
      if (resp && (resp.status === 'pending' || resp.status === 'processing')) {
        startMindmapPolling(id);
      }
    };

    void load();
  }, [aiTab, fetchMindmap, id, loading, startMindmapPolling]);

  useEffect(() => {
    return () => stopMindmapPolling();
  }, [stopMindmapPolling]);

  useEffect(() => {
    return () => stopSummaryPolling();
  }, [stopSummaryPolling]);

  useEffect(() => {
    return () => stopKeypointsPolling();
  }, [stopKeypointsPolling]);

  const fetchAudioUrl = useCallback(async (fileId: string) => {
    try {
      const response = await getAnonymousDownloadURL(fileId);
      setAudioUrl(response.download_url);
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch audio URL:', err);
      }
    }
  }, []);

  const handleAudioTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleAudioLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  const handleAudioPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleAudioPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const formatSegmentTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const handleSeek = useCallback((time: number) => {
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const handleSkipBack = useCallback(() => {
    const newTime = Math.max(0, currentTime - 10);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  }, [currentTime]);

  const handleSkipForward = useCallback(() => {
    const maxTime = audioDuration || transcription?.duration || 0;
    const newTime = Math.min(maxTime, currentTime + 10);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  }, [audioDuration, currentTime, transcription?.duration]);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((err) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('Failed to play audio:', err);
        }
      });
    }
  }, [isPlaying]);

  const handleSpeedChange = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  }, []);

  const handleRenameDisplayName = useCallback(
    async (nextName: string) => {
      if (!transcription) return;
      const previousDisplayName = transcription.display_name;

      setIsRenamingDisplayName(true);
      setTranscription((prev) => (prev ? { ...prev, display_name: nextName } : prev));
      try {
        const updated = await updateAnonymousTranscriptionDisplayName(transcription.id, nextName);
        setTranscription((prev) =>
          prev ? { ...prev, display_name: updated.transcription.display_name ?? nextName } : prev
        );
      } catch (err) {
        setTranscription((prev) => (prev ? { ...prev, display_name: previousDisplayName } : prev));
        showToastNotification('Failed to rename transcription');
        throw err;
      } finally {
        setIsRenamingDisplayName(false);
      }
    },
    [showToastNotification, transcription]
  );

  const handleSaveSegment = useCallback(
    async (segmentId: number, text: string) => {
      if (!id) return;
      const currentSegment = segments.find((segment) => segment.id === segmentId);
      if (!currentSegment) {
        showToastNotification('Segment not found. Please refresh.');
        return;
      }

      if (currentSegment.text === text) {
        setEditingSegmentId(null);
        setEditingText('');
        return;
      }

      try {
        setIsSaving(true);
        const response = await updateAnonymousTranscriptionContent(id, [{ id: segmentId, text }]);
        setSegments((prev) =>
          prev.map((segment) => (segment.id === segmentId ? { ...segment, text, is_modified: true } : segment))
        );
        setTranscription((prev) =>
          prev
            ? {
                ...prev,
                is_edited: response.is_edited,
                last_edited_at: response.last_edited_at,
              }
            : prev
        );
        setMindmapStatus('idle');
        setMindmapMarkdown(null);
        setMindmapError(null);
        stopMindmapPolling();
        showToastNotification('Segment updated');
        setEditingSegmentId(null);
        setEditingText('');
        await fetchAvailableTranslations(id);
      } catch (err) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('Failed to save segment edit:', err);
        }
        showToastNotification('Failed to save. Please try again.');
      } finally {
        setIsSaving(false);
      }
    },
    [fetchAvailableTranslations, id, segments, showToastNotification, stopMindmapPolling]
  );

  const handleRevertTranscription = useCallback(async () => {
    if (!id) return;
    try {
      setIsReverting(true);
      const response: RevertResponse = await revertAnonymousTranscription(id);
      const result = await getAnonymousTranscriptionResult(id);
      const nextSegments = mapAnonymousSegments(result, transcription?.duration ?? 0);
      segmentIndexByIdRef.current = new Map(nextSegments.map((segment, index) => [segment.id, index]));
      setSegments(nextSegments);
      setTranscription((prev) =>
        prev
          ? {
              ...prev,
              is_edited: response.is_edited,
              last_edited_at: undefined,
            }
          : prev
      );
      setMindmapStatus('idle');
      setMindmapMarkdown(null);
      setMindmapError(null);
      stopMindmapPolling();
      setEditingSegmentId(null);
      setEditingText('');
      setIsTranslationStale(false);
      showToastNotification(response.message || 'Transcript reverted to original');
      await fetchAvailableTranslations(id);
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Failed to revert transcription:', err);
      }
      showToastNotification('Failed to revert. Please try again.');
    } finally {
      setIsReverting(false);
      setShowRevertConfirm(false);
    }
  }, [fetchAvailableTranslations, id, showToastNotification, stopMindmapPolling, transcription?.duration]);

  const handleTranslate = useCallback(async () => {
    if (!id) return;
    const hadTranslation = segments.some((segment) => Boolean(segment.translation));
    setIsTranslating(true);
    setTranslationError(null);
    setTranslationProgress(0);

    try {
      await translateAnonymousTranscription(id, targetLanguage);
      showToastNotification(hadTranslation ? 'Translation restarted' : 'Translation started');
      startTranslationPolling(id, targetLanguage);
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === 'TRANSLATION_IN_PROGRESS' || code === 'translation_in_progress') {
        showToastNotification('Translation already in progress');
        startTranslationPolling(id, targetLanguage);
        return;
      }

      const message = handleApiError(err);
      setTranslationError(message);
      showToastNotification(message);
      setIsTranslating(false);
    }
  }, [id, segments, showToastNotification, startTranslationPolling, targetLanguage]);

  const handleExportSubmit = async (format: string, options: ExportOptions) => {
    let content = '';
    let mimeType = 'text/plain';
    let extension = format;

    try {
      setIsExporting(true);
      switch (format) {
        case 'txt':
          content = generateTXT(segments, options);
          mimeType = 'text/plain';
          break;
        case 'json':
          content = generateJSON(segments);
          mimeType = 'application/json';
          break;
        case 'md':
          content = generateMarkdown(segments, options);
          mimeType = 'text/markdown';
          break;
        case 'srt':
          content = generateSRT(segments);
          mimeType = 'text/plain';
          break;
        case 'vtt':
          content = generateVTT(segments);
          mimeType = 'text/vtt';
          break;
        case 'docx':
        case 'pdf':
          showToastNotification(`${format.toUpperCase()} export is coming soon!`);
          setIsExportOpen(false);
          return;
        default:
          showToastNotification('Unsupported export format');
          return;
      }

      const filenameBase =
        transcription?.display_name ?? `Trial Transcription #${id?.slice(-6) || transcription?.id.slice(-6) || ''}`;
      const filename = buildExportFilename(filenameBase, extension, transcription?.display_name_user_set !== true);
      downloadBlob(content, filename, mimeType);
      showToastNotification(`Exported as ${format.toUpperCase()}`);
      setIsExportOpen(false);
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Export failed:', err);
      }
      showToastNotification('Failed to export transcript');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    getAnonymousQuota()
      .then(setQuota)
      .catch(() => {
        // Non-fatal on this page.
      });
  }, []);

  useEffect(() => {
    if (!id) return;

    // Reset state when id changes
    resultLoadedRef.current = false;
    audioUrlLoadedRef.current = false;
    translationsLoadedRef.current = false;
    userChangedDisplayModeRef.current = false;
    translationUpdatesCursorRef.current = 0;
    segmentIndexByIdRef.current = new Map();

    setLoading(true);
    setError(null);
    setTranscription(null);
    setSegments([]);

    setIsExportOpen(false);
    setIsExporting(false);

    setAudioUrl(null);
    setAudioDuration(0);
    setIsPlaying(false);
    setCurrentTime(0);
    setPlaybackSpeed(1);
    setVolume(1);

    setEditingSegmentId(null);
    setEditingText('');
    setIsSaving(false);
    setIsReverting(false);
    setShowRevertConfirm(false);
    setIsRenamingDisplayName(false);

    setDisplayMode('bilingual');
    setTargetLanguage('en');
    setTranslationError(null);
    setIsTranslationOpen(false);
    setIsTranslating(false);
    setTranslationProgress(0);
    setAvailableTranslations([]);
    setIsTranslationStale(false);
    stopTranslationPolling();

    setMindmapStatus('idle');
    setMindmapMarkdown(null);
    setMindmapError(null);
    stopMindmapPolling();

    const fetchOnce = async () => {
      try {
        const res = await getAnonymousTranscription(id);
        setTranscription(res.transcription);
        setError(null);
        setLoading(false);

        const status = res.transcription.status;
        const isTerminal = ['failed', 'cancelled'].includes(status);
        const isCompleted = status === 'completed';

        if (isCompleted) {
          if (!resultLoadedRef.current) {
            try {
              const full = await getAnonymousTranscriptionResult(id);
              const nextSegments = mapAnonymousSegments(full, res.transcription.duration);
              segmentIndexByIdRef.current = new Map(nextSegments.map((segment, index) => [segment.id, index]));
              setSegments(nextSegments);
              resultLoadedRef.current = true;
            } catch (e) {
              // Result may not be available immediately after completion; keep polling.
              if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.warn('Failed to fetch trial result, will retry:', e);
              }
            }
          }

          if (res.transcription.file_id && !audioUrlLoadedRef.current) {
            audioUrlLoadedRef.current = true;
            void fetchAudioUrl(res.transcription.file_id);
          }

          if (!translationsLoadedRef.current) {
            translationsLoadedRef.current = true;
            const translations = await fetchAvailableTranslations(id);
            const completedTranslation = translations.find((t) => t.status === 'completed');
            const processingTranslation = translations.find((t) => t.status === 'processing' || t.status === 'pending');

            if (processingTranslation) {
              setTargetLanguage(processingTranslation.language);
              setIsTranslating(true);
              setTranslationProgress(processingTranslation.progress || 0);
              startTranslationPolling(id, processingTranslation.language);
            } else if (completedTranslation) {
              setTargetLanguage(completedTranslation.language);
              await fetchTranslationContent(id, completedTranslation.language);
            }
          }
        }

        if (isTerminal || (isCompleted && resultLoadedRef.current)) {
          if (pollingRef.current) {
            window.clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      } catch (err) {
        setError(handleApiError(err));
        setLoading(false);
        if (pollingRef.current) {
          window.clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    };

    void fetchOnce();
    pollingRef.current = window.setInterval(fetchOnce, pollIntervalMs);

    return () => {
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [
    fetchAudioUrl,
    fetchAvailableTranslations,
    fetchTranslationContent,
    id,
    startTranslationPolling,
    stopMindmapPolling,
    stopTranslationPolling,
  ]);

  // Load translation content when target language changes
  useEffect(() => {
    if (!id || loading) return;

    const translationItem = availableTranslations.find((t) => t.language === targetLanguage);
    const hasCompletedTranslationFile = translationItem?.status === 'completed';

    const checkLanguageStatus = async () => {
      try {
        const status = await getAnonymousTranslationStatus(id, targetLanguage);
        if (status.status === 'processing' || status.status === 'pending') {
          setIsTranslating(true);
          setTranslationProgress(status.progress || 0);
          if (hasCompletedTranslationFile) {
            await fetchTranslationContent(id, targetLanguage);
          } else {
            setSegments((prev) =>
              prev.map((segment) => ({
                ...segment,
                translation: undefined,
              }))
            );
            setIsTranslationStale(false);
          }
          startTranslationPolling(id, targetLanguage);
          return;
        }
        if (status.status === 'completed') {
          stopTranslationPolling();
          setIsTranslating(false);
          setTranslationProgress(100);
          await fetchTranslationContent(id, targetLanguage);
          await fetchAvailableTranslations(id);
          return;
        }
        if (status.status === 'failed') {
          stopTranslationPolling();
          setIsTranslating(false);
          setTranslationProgress(status.progress || 0);
          setTranslationError('Translation failed. Please try again.');
          return;
        }
      } catch {
        if (hasCompletedTranslationFile) {
          stopTranslationPolling();
          setIsTranslating(false);
          setTranslationProgress(100);
          await fetchTranslationContent(id, targetLanguage);
          return;
        }
      }

      stopTranslationPolling();
      setIsTranslating(false);
      setTranslationProgress(0);
      setSegments((prev) =>
        prev.map((segment) => ({
          ...segment,
          translation: undefined,
        }))
      );
      setIsTranslationStale(false);
    };

    void checkLanguageStatus();
  }, [
    availableTranslations,
    fetchAvailableTranslations,
    fetchTranslationContent,
    id,
    loading,
    startTranslationPolling,
    stopTranslationPolling,
    targetLanguage,
  ]);

  const fileNameNode = useMemo(() => {
    const fallback = `Trial Transcription #${id?.slice(-6) || ''}`;
    const rawName = transcription?.display_name || fallback;
    const value = transcription?.display_name_user_set
      ? (rawName.trim() || fallback)
      : (stripKnownTranscriptionTitleExtension(rawName) || fallback);
    return (
      <EditableFilename
        value={value}
        maxLength={255}
        isLoading={isRenamingDisplayName}
        onSave={handleRenameDisplayName}
        textClassName="text-lg font-semibold text-slate-900 dark:text-white"
        inputClassName="text-lg font-semibold text-slate-900 dark:text-white h-9"
      />
    );
  }, [handleRenameDisplayName, id, isRenamingDisplayName, transcription?.display_name]);

  if (!id) {
    return (
      <div className="h-[calc(100vh-32px)] flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Missing transcription id</h2>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-[calc(100vh-32px)] flex flex-col animate-in fade-in duration-500">
        <TranscriptionSkeletonHeader />
        <TranscriptionSkeletonBody />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[calc(100vh-32px)] flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Failed to load transcription</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Go Home
            </Button>
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const isProcessing = transcription && ['pending', 'queued', 'processing'].includes(transcription.status);
  const isFailed = transcription && transcription.status === 'failed';

  if (isProcessing) {
    return (
      <div className="h-[calc(100vh-32px)] flex flex-col animate-in fade-in duration-500">
        <TranscriptHeader
          fileName={fileNameNode}
          status={transcription?.status}
          startedAt={transcription?.started_at}
          createdAt={transcription?.created_at}
          progress={transcription?.progress}
          onBack={() => navigate('/')}
          onExport={() => setIsExportOpen(true)}
        />
        {quota && (
          <div className="px-1 pb-2">
            <TrialLimitBanner
              remainingFiles={quota.remaining_files}
              dailyLimit={quota.daily_limit}
              resetTime={quota.reset_at ? new Date(quota.reset_at) : undefined}
              showWhenFull={true}
            />
          </div>
        )}
        <TranscriptionSkeletonBody />
        <Toast message={toastMessage} isVisible={showToast} />
      </div>
    );
  }

  if (isFailed) {
    return (
      <div className="h-[calc(100vh-32px)] flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Transcription Failed</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {transcription?.error_message || 'An error occurred during transcription.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const isEdited = transcription?.is_edited ?? false;
  const currentLangTranslation = availableTranslations.find((t) => t.language === targetLanguage);
  const hasTranslationForCurrentLang = currentLangTranslation?.status === 'completed';
  const isCurrentLangStale = currentLangTranslation?.is_stale ?? isTranslationStale;
  const hasTranslationContent = segments.some((segment) => Boolean(segment.translation && segment.translation.trim()));
  const effectiveDisplayMode: TranslationDisplayMode =
    (hasTranslationForCurrentLang || hasTranslationContent) ? displayMode : 'original';

  return (
    <div className="h-[calc(100vh-32px)] flex flex-col animate-in fade-in duration-500">
      {/* Hidden Audio Element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleAudioTimeUpdate}
          onLoadedMetadata={handleAudioLoadedMetadata}
          onPlay={handleAudioPlay}
          onPause={handleAudioPause}
          onEnded={handleAudioEnded}
          preload="metadata"
        />
      )}
      <TranscriptHeader
        fileName={fileNameNode}
        processingTime={processingTimeSeconds}
        isEdited={isEdited}
        isTranslationStale={isCurrentLangStale && hasTranslationContent}
        onBack={() => navigate('/')}
        onExport={() => setIsExportOpen(true)}
      />

      {quota && (
        <div className="px-1 pb-2">
          <TrialLimitBanner
            remainingFiles={quota.remaining_files}
            dailyLimit={quota.daily_limit}
            resetTime={quota.reset_at ? new Date(quota.reset_at) : undefined}
            showWhenFull={true}
          />
        </div>
      )}

      <div ref={containerRef} className="flex-1 flex flex-col lg:flex-row min-h-0">
        <div
          className="w-full lg:flex-none flex flex-col bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
          style={{ flex: `0 0 ${leftPanelWidth}%` }}
        >
          <TranscriptToolbar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onCopyAll={handleCopyAll}
            showSpeakerButton={hasSpeakerInformation}
            onSpeakerClick={() => setIsSpeakerRenamePromptOpen(true)}
            isEdited={isEdited}
            isReverting={isReverting}
            onRevert={() => setShowRevertConfirm(true)}
            isTranslationOpen={isTranslationOpen}
            onToggleTranslationOpen={() => setIsTranslationOpen((prev) => !prev)}
            translationSettings={{
              targetLanguage,
              onTargetLanguageChange: setTargetLanguage,
              displayMode,
              onDisplayModeChange: (mode) => {
                userChangedDisplayModeRef.current = true;
                setDisplayMode(mode);
              },
              onTranslate: handleTranslate,
              isTranslating,
              translationProgress,
              hasTranslation: hasTranslationForCurrentLang,
              isTranslationStale: isCurrentLangStale,
              errorMessage: translationError,
              languageOptions: LANGUAGE_OPTIONS,
            }}
          />

          {showRating && <QualityRating onDismiss={() => setShowRating(false)} />}

          <div className="relative flex-1 min-h-0">
            <div ref={segmentsListRef} className="absolute inset-0 overflow-y-auto custom-scrollbar">
              {segments.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <div className="text-center">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No transcript segments available</p>
                  </div>
                </div>
              ) : (
                segments.map((segment) => (
                  <EditableSegment
                    key={segment.id}
                    segment={segment}
                    isActive={activeSegment === segment.id}
                    isEditing={editingSegmentId === segment.id}
                    editingText={editingText}
                    onTextChange={setEditingText}
                    onEdit={() => {
                      setEditingSegmentId(segment.id);
                      setEditingText(segment.text);
                      setActiveSegment(segment.id);
                    }}
                    onSave={(text) => void handleSaveSegment(segment.id, text)}
                    onCancel={() => {
                      setEditingSegmentId(null);
                      setEditingText('');
                    }}
                    onClick={() => setActiveSegment(segment.id)}
                    onPlay={() => {
                      if (!audioUrl) {
                        showToastNotification('Audio is still loading');
                        return;
                      }
                      handleSeek(segment.start);
                      if (audioRef.current) {
                        audioRef.current.play().catch((err) => {
                          if (import.meta.env.DEV) {
                            // eslint-disable-next-line no-console
                            console.warn('Failed to play audio:', err);
                          }
                        });
                      }
                    }}
                    formatTime={formatSegmentTime}
                    isSaving={isSaving && editingSegmentId === segment.id}
                    displayMode={effectiveDisplayMode}
                  />
                ))
              )}
            </div>
            <TranscriptScrollFab
              isScrollable={isScrollable}
              isAtBottom={isAtBottom}
              onScrollToTop={handleScrollToTop}
              onScrollToBottom={handleScrollToBottom}
            />
          </div>

          {showAccountBanner && (
            <TrialAccountBanner
              onDismiss={() => setShowAccountBanner(false)}
              onRegister={() => navigate('/register')}
              onLogin={() => navigate('/login')}
            />
          )}

          <AudioPlayerBar
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={audioDuration || transcription?.duration || 0}
            playbackSpeed={playbackSpeed}
            volume={volume}
            onPlayPause={handlePlayPause}
            onSkipBack={handleSkipBack}
            onSkipForward={handleSkipForward}
            onSeek={handleSeek}
            onSpeedChange={handleSpeedChange}
            onVolumeChange={handleVolumeChange}
          />
        </div>

        <PanelResizer onResize={handlePanelResize} />

        <div className="w-full min-h-[300px] lg:min-h-0 lg:flex-1">
          <AIInsightsPanel
            activeTab={aiTab}
            onTabChange={setAiTab}
            summaryTemplates={summaryTemplates}
            selectedSummaryTemplateId={selectedSummaryTemplateId}
            onSelectSummaryTemplateId={setSelectedSummaryTemplateId}
            summaryStatus={summaryStatus}
            summaryMarkdown={summaryMarkdown}
            summaryError={summaryError}
            onGenerateSummary={requestSummary}
            keypointsStatus={keypointsStatus}
            keypointsItems={keypointsItems}
            keypointsError={keypointsError}
            mindmapStatus={mindmapStatus}
            mindmapMarkdown={mindmapMarkdown}
            mindmapError={mindmapError}
            onGenerateMindmap={requestMindmap}
            onGenerateKeyPoints={requestKeypoints}
          />
        </div>
      </div>

      <Toast message={toastMessage} isVisible={showToast} />

      <ConfirmDialog
        isOpen={showRevertConfirm}
        onClose={() => setShowRevertConfirm(false)}
        onConfirm={handleRevertTranscription}
        title={t('transcription.toolbar.revertTranscriptTitle')}
        message={t('transcription.toolbar.revertTranscriptMessage')}
        confirmText={t('transcription.toolbar.revertConfirm')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={isReverting}
      />

      <Modal
        isOpen={isSpeakerRenamePromptOpen}
        onClose={() => setIsSpeakerRenamePromptOpen(false)}
        title={t('transcription.toolbar.renameSpeakersTitle')}
        footer={
          <>
            <Button variant="outline" onClick={() => navigate('/login')}>
              Sign in
            </Button>
            <Button onClick={() => navigate('/register')}>
              Create account
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-zinc-400">
          Create an account to rename speaker labels and keep your changes.
        </p>
      </Modal>

      <Modal isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} title={t('transcription.export.title')}>
        <ExportTranscript
          onExport={handleExportSubmit}
          onCancel={() => setIsExportOpen(false)}
          isExporting={isExporting}
        />
      </Modal>
    </div>
  );
}
